import  { useEffect, useState, useMemo } from 'react';
import { useGitStore } from '@/stores/git';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Plus, Minus, GitCommit, Loader2,  Sparkles, RotateCcw, Github, Code2, Asterisk, Send, Bot, RefreshCw } from 'lucide-react';
import { DiffEditor } from '@/components/editor/Monaco';
import { http } from '@/lib/http';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { useSettingsStore } from '@/stores/settings';
import { useAppStatusStore } from '@/stores/appStatus';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from '@/lib/utils';
import { useLongPress } from '@/hooks/use-long-press';

const FileDiffViewer = ({ path, staged }: { path: string; staged: boolean }) => {
const [diffContent, setDiffContent] = useState<{ original: string; modified: string } | null>(null);
const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchContent = async () => {
            setIsLoading(true);
            try {
                const data = await http<{ original: string; modified: string }>(
                    `/api/git/diff_content?path=${encodeURIComponent(path)}&staged=${staged}`
                );
                setDiffContent(data);
            } catch (e: any) {
                toast.error(`Failed to load diff for ${path}`, { description: e.message });
            } finally {
                setIsLoading(false);
            }
        };
        fetchContent();
    }, [path, staged]);

    if (isLoading) {
        return <div className="p-4 flex items-center justify-center h-48"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading diff...</div>;
    }

    if (!diffContent) {
        return <div className="p-4 text-center text-destructive">Could not load diff.</div>;
    }
    
    const height = Math.max(diffContent.original.split('\n').length, diffContent.modified.split('\n').length) * 19 + 40;
    const cappedHeight = Math.min(height, 400);

    return (
        <div className="border rounded-md my-1" style={{ height: `${cappedHeight}px` }}>
            <DiffEditor
                filePath={path}
                original={diffContent.original}
                modified={diffContent.modified}
                readOnly
            />
        </div>
    );
};


const GitFile = ({ path, status, onStage, onUnstage, onDiscard, onDiff }: { path: string; status: string; onStage?: () => void; onUnstage?: () => void; onDiscard?: () => void, onDiff: () => void; }) => (
    <div className="flex items-center justify-between group text-xs px-2 py-1 hover:bg-accent rounded-md">
        <button onClick={onDiff} className="truncate text-left flex-1 flex items-center gap-2">
            <Badge variant="outline" className="px-1.5 py-0 font-mono text-[10px]">{status}</Badge>
            <span>{path}</span>
        </button>
        <div className="flex items-center">
            {onDiscard && (
                 <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onDiscard} title="Discard Changes">
                    <RotateCcw className="h-4 w-4" />
                </Button>
            )}
            {onUnstage && (
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onUnstage} title="Unstage">
                    <Minus className="h-4 w-4" />
                </Button>
            )}
            {onStage && (
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onStage} title="Stage">
                    <Plus className="h-4 w-4" />
                </Button>
            )}
        </div>
    </div>
);

const ProviderIcon = ({ provider }: { provider: string }) => {
  const props = { className: "h-4 w-4" };
  switch (provider) {
    case "gemini":
      return <Sparkles {...props} />;
    case "copilot":
      return <Github {...props} />;
    case "codex":
      return <Code2 {...props} />;
    case "claude":
      return <Asterisk {...props} />;
    case "qwen":
      return <Bot {...props} />;
    case "custom":
      return <Send {...props} />;
    default:
      return <Sparkles {...props} />;
  }
};

const ProviderMenuItem = ({
  provider,
  isSelected,
  onSelect,
}: {
  provider: { id: string; name: string; isCustom?: boolean };
  isSelected: boolean;
  onSelect: () => void;
}) => {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full flex items-center gap-2 px-2 py-2 text-sm hover:bg-accent",
        isSelected && "bg-accent"
      )}
    >
      <ProviderIcon provider={provider.isCustom ? "custom" : provider.id} />
      <span>{provider.name}</span>
    </button>
  );
};

// Helper to extract provider info from interaction mode
const getProviderInfo = (interactionMode: string, settings: any) => {
  if (interactionMode.startsWith("llm_provider:")) {
    const providerId = interactionMode.split(":")[1];
    const provider = settings.llmProviders?.[providerId];
    return provider ? { ...provider, isCustom: true } : null;
  }
  if (
    settings.workspace.interactionMode === "llm_provider" &&
    settings.workspace.llmProviderId
  ) {
    const provider = settings.llmProviders?.[settings.workspace.llmProviderId];
    return provider ? { ...provider, isCustom: true } : null;
  }
  return {
    name: interactionMode.charAt(0).toUpperCase() + interactionMode.slice(1),
    isCustom: false,
  };
};

export const GitPanel = () => {
    const { status, commitMessage, isLoading, isGeneratingCommitMessage, fetchStatus, stageFiles, unstageFiles, discardUnstagedChanges, discardUntrackedFiles, commit, setCommitMessage, createProposalFromStaged, aheadBehind, syncWithOrigin } = useGitStore();
    const [activeDiff, setActiveDiff] = useState<string | null>(null);
    const [isProviderMenuOpen, setIsProviderMenuOpen] = useState(false);

    const settings = useSettingsStore();
    const { toolAvailability } = useAppStatusStore();
    const llmProviders = useSettingsStore((state) => state.llmProviders);

    let { interactionMode, llmProviderId: workspaceLlmProviderId } = settings.workspace;
    if (interactionMode === 'copy') {
        interactionMode = 'qwen'; // Default to qwen if main mode is copy/yolo
    }

    const providerInfo = getProviderInfo(interactionMode, settings);
    const currentProviderId = useMemo(() => (providerInfo?.isCustom ? workspaceLlmProviderId : interactionMode) || 'qwen', [interactionMode, workspaceLlmProviderId, providerInfo]);
    const currentProviderName = useMemo(() => providerInfo?.name || 'Qwen', [providerInfo]);
    const providerIconKey = useMemo(() => providerInfo?.isCustom ? "custom" : interactionMode, [providerInfo, interactionMode]);

    const availableProviders = useMemo(() => {
        const providers = [];
        if (toolAvailability?.qwen) providers.push({ id: "qwen", name: "Qwen" });
        if (toolAvailability?.gemini) providers.push({ id: "gemini", name: "Gemini" });
        if (toolAvailability?.claude) providers.push({ id: "claude", name: "Claude" });
        if (toolAvailability?.codex) providers.push({ id: "codex", name: "Codex" });
        if (toolAvailability?.copilot) providers.push({ id: "copilot", name: "Copilot" });
        Object.values(llmProviders || {}).forEach((p) => {
            if (p.enabled) providers.push({ id: p.id, name: p.name, isCustom: true });
        });
        return providers;
    }, [toolAvailability, llmProviders]) as { id: string; name: string; isCustom?: boolean; }[];

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    const toggleDiff = (path: string) => {
        setActiveDiff(prev => (prev === path ? null : path));
    };
    
    const handleGenerateMessage = () => {
        createProposalFromStaged(currentProviderId);
    };

    const handleProviderSelect = (provider: { id: string }) => {
        createProposalFromStaged(provider.id);
        setIsProviderMenuOpen(false);
    };

    const longPressEvents = useLongPress(() => setIsProviderMenuOpen(true), { ms: 500 });

    if (isLoading && !status) {
        return <div className="h-full flex items-center justify-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Loading Git status...</div>;
    }

    if (!status) {
        return <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Could not load Git status.</div>;
    }
    
    const hasChanges = status.staged.length > 0 || status.unstaged.length > 0 || status.untracked.length > 0;
    const hasUncommittedChanges = status.staged.length > 0 || status.unstaged.length > 0;
    
    return (
        <div className="h-full flex flex-col gap-2 p-1">
            <div className="p-1 space-y-2">
                <Textarea 
                    placeholder="Commit message" 
                    value={commitMessage}
                    onChange={(e) => setCommitMessage(e.target.value)}
                    className="text-xs min-h-[60px]"
                />
                <div className="grid grid-cols-2 gap-2 relative">
                    <Button
                        onClick={handleGenerateMessage}
                        onContextMenu={(e) => { e.preventDefault(); setIsProviderMenuOpen(true); }}
                        {...longPressEvents}
                        variant="outline"
                        disabled={status.staged.length === 0 || isGeneratingCommitMessage}
                        className="w-full h-8"
                    >
                        {isGeneratingCommitMessage ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ProviderIcon provider={providerIconKey} />}
                        {isGeneratingCommitMessage ? "Generating..." : currentProviderName}
                    </Button>


                    <Button onClick={commit} disabled={status.staged.length === 0 || isGeneratingCommitMessage} className="w-full h-8">
                        <GitCommit className="h-4 w-4 mr-2" />
                        Commit
                    </Button>

                </div>
                                    <Popover open={isProviderMenuOpen} onOpenChange={setIsProviderMenuOpen}>
                        <PopoverTrigger asChild>
                        <div className="w-0 h-0 overflow-hidden" />
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-1" align="start">
                            <div className="text-xs font-medium text-muted-foreground mb-2 px-2">
                                Select Provider
                            </div>
                             {availableProviders.map((provider) => (
                                <ProviderMenuItem
                                    key={provider.id}
                                    provider={provider}
                                    isSelected={provider.id === currentProviderId}
                                    onSelect={() => handleProviderSelect(provider)}
                                />
                            ))}
                        </PopoverContent>
                    </Popover>
            </div>
            <ScrollArea className="flex-1 min-h-0">
                {!hasChanges ? (
                    <div className="text-center text-sm text-muted-foreground p-4">No changes detected.</div>
                ) : (
                    <Accordion type="multiple" defaultValue={['staged', 'unstaged', 'untracked']} className="w-full">
                        {status.staged.length > 0 && (
                            <AccordionItem value="staged">
                                <AccordionTrigger className="text-sm py-2 px-2">Staged Changes ({status.staged.length})</AccordionTrigger>
                                <AccordionContent className="pb-1">
                                    <div className="flex justify-end p-1 border-b mb-1">
                                        <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => unstageFiles(status.staged.map(f => f.path))}>Unstage All</Button>
                                    </div>
                                    {status.staged.map(file => (
                                        <div key={file.path}>
                                            <GitFile path={file.path} status={file.status} onUnstage={() => unstageFiles([file.path])} onDiff={() => toggleDiff(`staged-${file.path}`)} />
                                            {activeDiff === `staged-${file.path}` && <FileDiffViewer path={file.path} staged={true} />}
                                        </div>
                                    ))}
                                </AccordionContent>
                            </AccordionItem>
                        )}
                         {status.unstaged.length > 0 && (
                            <AccordionItem value="unstaged">
                                <AccordionTrigger className="text-sm py-2 px-2">Unstaged Changes ({status.unstaged.length})</AccordionTrigger>
                                <AccordionContent className="pb-1">
                                     <div className="flex justify-end p-1 border-b mb-1 gap-2">
                                        <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => discardUnstagedChanges(status.unstaged.map(f => f.path))}>Discard All</Button>
                                        <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => stageFiles(status.unstaged.map(f => f.path))}>Stage All</Button>
                                    </div>
                                    {status.unstaged.map(file => (
                                        <div key={file.path}>
                                            <GitFile
                                                path={file.path}
                                                status={file.status}
                                                onStage={() => stageFiles([file.path])}
                                                onDiscard={() => discardUnstagedChanges([file.path])}
                                                onDiff={() => toggleDiff(`unstaged-${file.path}`)}
                                            />
                                            {activeDiff === `unstaged-${file.path}` && <FileDiffViewer path={file.path} staged={false} />}
                                        </div>
                                    ))}
                                </AccordionContent>
                            </AccordionItem>
                        )}
                         {status.untracked.length > 0 && (
                            <AccordionItem value="untracked">
                                <AccordionTrigger className="text-sm py-2 px-2">Untracked ({status.untracked.length})</AccordionTrigger>
                                <AccordionContent className="pb-1">
                                    <div className="flex justify-end p-1 border-b mb-1 gap-2">
                                        <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => discardUntrackedFiles(status.untracked.map(f => f.path))}>Discard All</Button>
                                        <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => stageFiles(status.untracked.map(f => f.path))}>Stage All</Button>
                                    </div>
                                    {status.untracked.map(file => (
                                        <div key={file.path}>
                                            <GitFile
                                                path={file.path} status={file.status}
                                                onStage={() => stageFiles([file.path])}
                                                onDiscard={() => discardUntrackedFiles([file.path])}
                                                onDiff={() => toggleDiff(`untracked-${file.path}`)} />
                                            {activeDiff === `untracked-${file.path}` && <FileDiffViewer path={file.path} staged={false} />}
                                        </div>
                                    ))}
                                </AccordionContent>
                            </AccordionItem>
                        )}
                    </Accordion>
                )}
            </ScrollArea>
            <div className="p-1 border-t mt-auto flex-shrink-0">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="w-full">
                            <Button onClick={syncWithOrigin} variant="outline" className="w-full h-8" disabled={hasUncommittedChanges || isLoading || isGeneratingCommitMessage}>
                                {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                                <span>Sync Changes</span>
                                {aheadBehind && (
                                    <div className="flex items-center gap-1 text-xs ml-auto">
                                        <span>{aheadBehind.behind}↓</span>
                                        <span>{aheadBehind.ahead}↑</span>
                                    </div>
                                )}
                            </Button>
                        </div>
                    </TooltipTrigger>
                    {hasUncommittedChanges && <TooltipContent><p>Commit or stash your changes before syncing.</p></TooltipContent>}
                </Tooltip>
            </div>
        </div>
    );
};